import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { filter } from 'rxjs/operators';

import { Space, SpaceDefinition, SPACES, spaceFromUrl, spaceOf } from '../../access/space';
import { AuthService } from './auth.service';

/**
 * Quels espaces le jeton ouvre, lequel est ouvert, et lequel rouvrir.
 *
 * <p>Les espaces se <b>déduisent</b> du jeton, ils ne se configurent pas :
 * Administration pour le superviseur, Enseignant pour le professeur, Élève pour
 * l'élève, Inspection pour qui porte `sessions.inspect`. Le dernier choix est
 * mémorisé par compte, dans le navigateur — c'est un confort, pas un droit :
 * le garde de route et le serveur redemandent chacun le leur.</p>
 */
@Injectable({ providedIn: 'root' })
export class SpaceService {
  private readonly auth = inject(AuthService);
  private readonly keycloak = inject(KeycloakService);
  private readonly router = inject(Router);

  private readonly url = signal(this.router.url);

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.url.set(e.urlAfterRedirects));
  }

  /** Les espaces que le jeton ouvre, dans l'ordre d'affichage. */
  available(): SpaceDefinition[] {
    return SPACES.filter((s) => this.opens(s.id));
  }

  /** L'espace où l'on se trouve, d'après l'URL — nul hors de tout espace. */
  readonly current = computed<Space | null>(() => spaceFromUrl(this.url()));

  /** Où rouvrir à la connexion : le dernier espace choisi, s'il est encore ouvert. */
  remembered(): Space | null {
    try {
      const saved = localStorage.getItem(this.key()) as Space | null;
      return saved && this.opens(saved) ? saved : null;
    } catch {
      return null;
    }
  }

  remember(space: Space): void {
    try {
      localStorage.setItem(this.key(), space);
    } catch {
      // Stockage indisponible (navigation privée) : on retombe sur le choix à chaque connexion.
    }
  }

  /** Entre dans un espace et s'en souvient. */
  switchTo(space: Space, remember = true): Promise<boolean> {
    if (remember) this.remember(space);
    return this.router.navigateByUrl(spaceOf(space).home);
  }

  private opens(space: Space): boolean {
    switch (space) {
      // L'espace s'ouvre par le rôle de base ; ce qu'on y voit dépend du
      // profil — un administrateur sans profil n'y a que le tableau de bord.
      case 'admin': return this.auth.hasRole('supervisor') || this.auth.hasRole('admin');
      case 'teacher': return this.auth.hasRole('professor');
      case 'inspection': return this.auth.hasPermission('sessions.inspect');
      case 'student': return this.auth.hasRole('student');
    }
  }

  private key(): string {
    const sub = this.keycloak.getKeycloakInstance()?.tokenParsed?.sub ?? 'anonymous';
    return `space:${sub}`;
  }
}
